'use client'

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { getSystemResources, SystemResources } from "@/app/actions/monitoring"
import { Cpu, MemoryStick, HardDrive, Server, RefreshCw, Clock } from "lucide-react"
import { Spinner } from "@/components/ui/spinner"

interface ProgressBarProps {
    value: number;
    className?: string;
}

function ProgressBar({ value, className = "" }: ProgressBarProps) {
    const getColor = () => {
        if (value >= 90) return "bg-red-500";
        if (value >= 70) return "bg-amber-500";
        return "bg-emerald-500";
    };

    return (
        <div className={`h-3 w-full bg-secondary rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full transition-all duration-500 ease-out ${getColor()}`}
                style={{ width: `${Math.min(value, 100)}%` }}
            />
        </div>
    );
}

function formatBytes(mb: number): string {
    if (mb >= 1024) {
        return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${mb} MB`;
}

export function ServerResources() {
    const [resources, setResources] = useState<SystemResources | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState<number>(5);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchResources = useCallback(async (showRefreshIndicator = false) => {
        if (showRefreshIndicator) {
            setIsRefreshing(true);
        }
        try {
            const data = await getSystemResources();
            setResources(data);
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Failed to fetch system resources:', error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchResources();
    }, [fetchResources]);

    useEffect(() => {
        if (refreshInterval === 0) return;

        const interval = setInterval(() => {
            fetchResources();
        }, refreshInterval * 1000);

        return () => clearInterval(interval);
    }, [refreshInterval, fetchResources]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Spinner />
            </div>
        );
    }

    if (!resources) {
        return (
            <div className="max-w-4xl mx-auto text-center py-20 text-muted-foreground">
                Failed to load system resources
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
            {/* Header with controls */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 sm:p-2 bg-violet-100 dark:bg-violet-500/20 rounded-lg shrink-0">
                        <Server className="h-4 w-4 sm:h-5 sm:w-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-semibold text-foreground truncate">{resources.hostname}</h2>
                        <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3 shrink-0" />
                            <span className="truncate">{resources.uptime}</span>
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                    <Select
                        value={refreshInterval.toString()}
                        onValueChange={(value) => setRefreshInterval(parseInt(value))}
                    >
                        <SelectTrigger className="w-16 sm:w-20 h-8 text-xs sm:text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">Off</SelectItem>
                            <SelectItem value="3">3s</SelectItem>
                            <SelectItem value="5">5s</SelectItem>
                            <SelectItem value="10">10s</SelectItem>
                            <SelectItem value="30">30s</SelectItem>
                            <SelectItem value="60">1m</SelectItem>
                        </SelectContent>
                    </Select>

                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => fetchResources(true)}
                        disabled={isRefreshing}
                        title={lastUpdate ? `Updated: ${lastUpdate.toLocaleTimeString()}` : 'Refresh'}
                    >
                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Main metrics cards */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* CPU Card */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-500/20 rounded">
                                <Cpu className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            CPU Usage
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Usage</span>
                                <span className="font-mono font-medium text-foreground">{resources.cpu.usedPercent}%</span>
                            </div>
                            <ProgressBar value={resources.cpu.usedPercent} />
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Cores</p>
                                <p className="text-lg font-semibold text-foreground">{resources.cpu.cores}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Load Average</p>
                                <div className="flex gap-2 text-sm font-mono">
                                    <span className="text-blue-600 dark:text-blue-400">{resources.cpu.loadAverage.oneMin}</span>
                                    <span className="text-muted-foreground">/</span>
                                    <span className="text-cyan-600 dark:text-cyan-400">{resources.cpu.loadAverage.fiveMin}</span>
                                    <span className="text-muted-foreground">/</span>
                                    <span className="text-teal-600 dark:text-teal-400">{resources.cpu.loadAverage.fifteenMin}</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Memory Card */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <div className="p-1.5 bg-purple-100 dark:bg-purple-500/20 rounded">
                                <MemoryStick className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            Memory Usage
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                    Used: {formatBytes(resources.memory.used)} / {formatBytes(resources.memory.total)}
                                </span>
                                <span className="font-mono font-medium text-foreground">{resources.memory.usedPercent}%</span>
                            </div>
                            <ProgressBar value={resources.memory.usedPercent} />
                        </div>

                        <div className="grid grid-cols-3 gap-4 pt-2">
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Free</p>
                                <p className="text-sm font-medium text-foreground">{formatBytes(resources.memory.free)}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Available</p>
                                <p className="text-sm font-medium text-foreground">{formatBytes(resources.memory.available)}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Cached</p>
                                <p className="text-sm font-medium text-foreground">{formatBytes(resources.memory.cached)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Disk Usage Card */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <div className="p-1.5 bg-amber-100 dark:bg-amber-500/20 rounded">
                            <HardDrive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        Disk Usage
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {resources.disks.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No disk information available</p>
                    ) : (
                        <div className="space-y-4">
                            {resources.disks.map((disk, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <code className="text-xs bg-muted text-foreground px-2 py-0.5 rounded truncate max-w-[200px]" title={disk.mountpoint}>
                                                {disk.mountpoint}
                                            </code>
                                            <span className="text-xs text-muted-foreground truncate" title={disk.filesystem}>
                                                ({disk.filesystem})
                                            </span>
                                        </div>
                                        <div className="text-sm text-right whitespace-nowrap">
                                            <span className="text-muted-foreground">{disk.used}</span>
                                            <span className="text-muted-foreground/50"> / </span>
                                            <span className="text-foreground">{disk.size}</span>
                                            <span className="ml-2 font-mono font-medium text-foreground">{disk.usedPercent}%</span>
                                        </div>
                                    </div>
                                    <ProgressBar value={disk.usedPercent} />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
